// @flow

import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LocalForage from 'localforage';
import { Alert, Badge, Col, Form, Row } from 'react-bootstrap';
import { compose } from 'redux';
import { Button, MissingPasswordModal } from 'Components';
import { AppConfig } from 'Config';
import { translate, withAuth, withReduxForm, withReduxModals } from 'Hoc';
import { Forms, Modals } from 'Reducers';
import { checkEmail, getAuthError, login, swapToken } from '../Services/Auth';
import { pursueLogin } from '../Services/Login';
import {
  checkedEmailKey,
  getSsoToken,
  signInWithSSO,
  ssoLogout,
  SsoTokenErrors,
} from '../Services/SSO';

type LoginFormPropsType = {
  onFieldValueChange: (string, string) => void,
  onSubmit: () => void,
  hideModal: (modalName: string) => void,
  showModal: (modalName: string) => void,
  t: string => string,
  setLoginFormValue: (string, string) => void,
  loginForm: LoginType,
  modals: ModalsStateType,
  isLoginFormValid: LoginFormType => boolean,
  clearLoginForm: () => void,
  setPartnerHeaderCode: string => void,
  className?: string,
};

type LoginFormStateType = {
  mutationError: ?ErrorType,
  ssoStep: string,
  isSsoLoading: boolean,
  isLoginLoading: boolean,
};

const SSO_STEPS = {
  NOT_CHECKED: 'NOT_CHECKED',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
};

class LoginForm extends Component<LoginFormPropsType, LoginFormStateType> {
  handleEmailValueChange: (SyntheticEvent<*>) => Promise<*>;

  handlePasswordValueChange: (SyntheticEvent<*>) => Promise<*>;

  handleMissingPasswordShowModal: (SyntheticEvent<*>) => Promise<*>;

  handleMissingPasswordHideModal: (SyntheticEvent<*>) => Promise<*>;

  static defaultProps = {
    className: undefined,
  };

  constructor(props) {
    super(props);
    const { showModal, hideModal, clearLoginForm } = props;

    this.handleMissingPasswordShowModal = showModal.bind(this, Modals.missingPassword);
    this.handleMissingPasswordHideModal = hideModal.bind(this, Modals.missingPassword);
    this.handleEmailValueChange = this.handleFieldChange.bind(this, 'email');
    this.handlePasswordValueChange = this.handleFieldChange.bind(this, 'password');
    this.state = {
      mutationError: undefined,
      ssoStep: SSO_STEPS.NOT_CHECKED,
      isSsoLoading: false,
      isLoginLoading: false,
    };
    clearLoginForm();
  }

  handleAuthError = (error: Error) => {
    const { t } = this.props;

    this.setState({ mutationError: getAuthError(t, error) });
  };

  showLoader() {
    this.setState({
      isSsoLoading: true,
    });
  }

  hideLoader() {
    this.setState({
      isSsoLoading: false,
    });
  }

  async handleFieldChange(field, event) {
    const { setLoginFormValue } = this.props;
    const {
      currentTarget: { value },
    } = event;

    await setLoginFormValue(field, value);
  }

  tryLoginWithSso = async () => {
    this.showLoader();
    const {
      loginForm: { values },
      setPartnerHeaderCode,
    } = this.props;

    let result;
    try {
      result = await checkEmail(values.email || '');
      await LocalForage.setItem(checkedEmailKey, values.email);
    } catch (error) {
      this.handleAuthError(error);
      this.hideLoader();

      return;
    }

    const { found, idSSO, standardSSO } = result;

    if (found && idSSO && standardSSO) {
      let ssoToken;
      try {
        await signInWithSSO(idSSO, standardSSO);
        ssoToken = await getSsoToken();
      } catch (error) {
        await this.handleGetSsoTokenError(error);

        return;
      }

      try {
        await swapToken(ssoToken);
        const partnerCode = await pursueLogin();
        await setPartnerHeaderCode(partnerCode);
      } catch (error) {
        this.handleAuthError(error);
        await ssoLogout();
        this.setState({ ssoStep: SSO_STEPS.NOT_AVAILABLE });
        this.hideLoader();
      }

      return;
    }

    this.setState({ ssoStep: SSO_STEPS.NOT_AVAILABLE });
    this.hideLoader();
  };

  async handleGetSsoTokenError(error) {
    this.hideLoader();
    await LocalForage.removeItem(checkedEmailKey);

    const { showModal } = this.props;
    switch (error.message) {
      case SsoTokenErrors.NOT_SAME_EMAIL:
        showModal(Modals.notSameLoginEmail);
        await ssoLogout();
        break;
      case SsoTokenErrors.NO_TOKEN:
        this.setState({
          ssoStep: SSO_STEPS.NOT_CHECKED,
        });
        console.warn('❌ No token available');
        break;

      default:
        console.warn('❌ Unhandled error', error);
        this.handleAuthError(error);
    }
  }

  handleSubmit = async () => {
    const {
      isLoginFormValid,
      loginForm: { values },
      setPartnerHeaderCode,
    } = this.props;

    const isFormValid = await isLoginFormValid(values);

    if (!isFormValid) {
      return;
    }

    this.setState({ isLoginLoading: true });
    try {
      await login(values.email || '', values.password || '');
      const partnerCode = await pursueLogin();
      await setPartnerHeaderCode(partnerCode);
    } catch (error) {
      this.handleAuthError(error);
      this.setState({ isLoginLoading: false });
    }
  };

  handleCloseAlert = () => this.setState({ mutationError: undefined });

  handlePreventSubmit = event => event.preventDefault();

  render() {
    const {
      t,
      loginForm: { values, errors },
      modals: { [Modals.missingPassword]: missingPasswordModalVisible },
    } = this.props;
    const { mutationError, ssoStep, isSsoLoading, isLoginLoading } = this.state;

    return (
      <>
        <Form autoComplete="off" onSubmit={this.handlePreventSubmit} className="w-100">
          <Alert
            dismissible
            variant="danger"
            show={typeof mutationError !== 'undefined'}
            onClose={this.handleCloseAlert}
          >
            <Alert.Heading>{t('errorOccurred')}</Alert.Heading>
            <p>{mutationError ? mutationError.message : ''}</p>
          </Alert>

          <Form.Group controlId="email">
            {ssoStep === SSO_STEPS.NOT_AVAILABLE ? ( // This will prevent the user from bypassing check email step
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <p style={{ margin: 'auto 0' }}>{values.email}</p>
                <Button
                  size="sm"
                  variant="transparent"
                  onClick={() => {
                    this.setState({
                      ssoStep: SSO_STEPS.NOT_CHECKED,
                    });
                  }}
                >
                  <FontAwesomeIcon icon="pen" size="sm" />
                </Button>
              </div>
            ) : (
              <>
                <Form.Control
                  autoComplete="new-email"
                  type="email"
                  value={values.email || ''}
                  placeholder={t('emailAddress')}
                  onChange={this.handleEmailValueChange}
                  isInvalid={typeof errors.email === 'string'}
                />
                <Form.Text className="text-muted">{t('emailAddressHelper')}</Form.Text>
                <Form.Control.Feedback type="invalid">
                  {t(errors.email || '')}
                </Form.Control.Feedback>
              </>
            )}
          </Form.Group>

          {ssoStep === SSO_STEPS.NOT_AVAILABLE && (
            <Form.Group controlId="password">
              <Form.Label>{t('password')}</Form.Label>
              <Form.Control
                autoComplete="new-password"
                type="password"
                value={values.password || ''}
                placeholder={t('password')}
                onChange={this.handlePasswordValueChange}
                isInvalid={typeof errors.password === 'string'}
              />
              <Form.Control.Feedback type="invalid">
                {t(errors.password || '')}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Row noGutters>
            <Col>
              {ssoStep === SSO_STEPS.NOT_AVAILABLE ? (
                <Button
                  type="submit"
                  onClick={this.handleSubmit}
                  disabled={isLoginLoading}
                  loading={isLoginLoading}
                  block
                >
                  {t('login')}
                </Button>
              ) : (
                <Button
                  type="submit"
                  onClick={this.tryLoginWithSso}
                  disabled={isSsoLoading}
                  loading={isSsoLoading}
                  block
                >
                  {t('next')}
                </Button>
              )}
            </Col>
            {ssoStep === SSO_STEPS.NOT_AVAILABLE && (
              <Col>
                <Button variant="link" onClick={this.handleMissingPasswordShowModal} block>
                  <u>{t('missingPassword')}</u>
                </Button>
              </Col>
            )}
          </Row>
        </Form>
        <MissingPasswordModal
          show={missingPasswordModalVisible}
          onHide={this.handleMissingPasswordHideModal}
          onExit={this.handleMissingPasswordHideModal}
        />
        {AppConfig.isStaging && (
          <Badge variant="light" className="my-2">
            v{AppConfig.persistence.schemaVersion}
          </Badge>
        )}
      </>
    );
  }
}

export default compose(
  withReduxForm(Forms.login),
  withReduxModals(),
  withAuth(),
  translate(['login_form', 'form_errors', 'gql_errors']),
)(LoginForm);
